// Leitura do arquivo de importação de OS. Puro: sem DOM — dá para testar direto.
// Espelha o formato de `_precos-parse.ts`: linhas de texto, delimitador
// auto-detectado, colunas nomeiam entidades.
//
// **Uma linha por OS.** O cabeçalho tem as 6 colunas fixas em qualquer ordem —
// `cliente, os, origem, data, hora, sacos` — e, depois delas, uma coluna para
// cada Tipo de Peça (só os que a OS usa; não precisa listar todos). A célula sob
// uma coluna de tipo é a **quantidade recebida**; vazia = o tipo não veio nessa
// OS.
//
// Duas camadas de erro: a OS inteira é inválida quando o cabeçalho dela não dá
// para lançar o Lote — Hotel, OS, origem, data, hora ou sacos ruins, ou a OS já
// existir (no sistema ou repetida no arquivo). Uma célula é só ignorada quando a
// quantidade não vale — o resto da OS entra. Uma coluna de tipo que não casa
// nenhum Tipo de Peça ativo é ignorada para o arquivo todo, com aviso.
//
// A coluna `origem` é só um sinalizador ("veio da rotina" vs. "coleta avulsa"):
// o texto dela não é comparado com nada. Quando não é vazia nem "evento
// pontual", casa-se uma Parada de coleta do hotel pelo dia da semana da data
// (e, se a `hora` vier preenchida, também pelo horário).

import type { EntradaLoteComando } from "../../comandos/lotes.ts";
import { diaSemanaISO, horaDe, paraDataISOBr } from "../../domain/datas.ts";
import { descreveDias, paradasDoCliente } from "../../domain/paradas.ts";
import { OS_MAX, OS_MIN } from "../../domain/lotes.ts";
import type { Snapshot } from "../../domain/snapshot.ts";

function norm(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

const HORA = /^([01]\d|2[0-3]):[0-5]\d$/;

/** As colunas que descrevem a OS — vêm antes das colunas de tipo de peça. */
const COLUNAS_FIXAS = ["cliente", "os", "origem", "data", "hora", "sacos"] as const;
type ColunaFixa = (typeof COLUNAS_FIXAS)[number];

export interface ItemOSValido {
  tipoId: string;
  tipoNome: string;
  quantidade: number;
}

/** Uma célula de quantidade que não deu para ler — a OS entra sem ela. */
export interface ItemOSIgnorado {
  tipo: string;
  motivo: string;
}

/** Uma coluna depois das fixas que não casou um Tipo de Peça ativo. */
export interface ColunaIgnorada {
  rotulo: string;
  motivo: string;
}

/** Uma OS que pode ser lançada: cabeçalho validado, um item por coluna preenchida. */
export interface GrupoOSValido {
  valido: true;
  numeroLinha: number;
  os: number;
  clienteId: string;
  clienteNome: string;
  /** Parada de coleta casada pela rotina do cliente; `null` = Coleta Avulsa. */
  paradaId: string | null;
  origemNome: string;
  coletaData: string;
  coletaHora: string;
  sacos: number | null;
  relavagem: boolean;
  itens: ItemOSValido[];
  itensIgnorados: ItemOSIgnorado[];
}

/** Uma OS que não dá para lançar — a linha inteira é ignorada. */
export interface GrupoOSInvalido {
  valido: false;
  numeroLinha: number;
  /** Cru, como veio do arquivo — pode não ser um número. */
  os: string;
  motivo: string;
}

export type GrupoOS = GrupoOSValido | GrupoOSInvalido;

export interface AnaliseImportacaoOS {
  grupos: GrupoOS[];
  /** Colunas de tipo do arquivo que foram ignoradas (não casaram / repetidas). */
  colunasIgnoradas: ColunaIgnorada[];
  /** Erro que impede qualquer leitura — cabeçalho sem as colunas fixas. */
  erroArquivo?: string;
}

/**
 * Traduz a análise nas entradas do comando `lote.lancarEmMassa`.
 *
 * Uma decisão de política mora aqui: **OS importada nunca nasce urgente nem com
 * prazo previsto manual**. Quem quiser prazo diferente edita a OS depois.
 */
export function entradasDaAnalise(a: AnaliseImportacaoOS): EntradaLoteComando[] {
  return a.grupos
    .filter((g): g is GrupoOSValido => g.valido)
    .map((g) => ({
      dados: {
        clienteId: g.clienteId,
        os: g.os,
        coletaData: g.coletaData,
        coletaHora: g.coletaHora,
        paradaId: g.paradaId,
        sacos: g.sacos,
        urgente: false,
        previstoManual: null,
        relavagem: g.relavagem,
      },
      recebidas: g.itens.map((item) => ({
        tipoId: item.tipoId,
        quantidade: item.quantidade,
      })),
    }));
}

/**
 * Quantas OS de fato serão lançadas se a análise for aplicada. Deriva de
 * `entradasDaAnalise` para não haver duas definições de "válido" que possam
 * divergir — a prévia diria "8 OS" e o comando lançaria 7.
 */
export function contarOSValidas(a: AnaliseImportacaoOS): number {
  return entradasDaAnalise(a).length;
}

/**
 * Modelo de arquivo para o botão "Baixar modelo" — as colunas fixas + uma
 * coluna para cada Tipo de Peça ativo (por conveniência; quem for usar apaga as
 * que não precisa) + duas linhas de exemplo.
 */
export function modeloImportacaoOSCsv(snap: Pick<Snapshot, "tiposPeca">): string {
  const tipos = snap.tiposPeca
    .filter((t) => t.ativo)
    .map((t) => t.nome)
    .sort((a, b) => a.localeCompare(b, "pt-BR"));
  const cab = [...COLUNAS_FIXAS, ...tipos];
  const ex1 = ["HCP", "1044", "rotina", "04/09/2026", "08:00", "3"];
  const ex2 = ["PSV", "2201", "", "04/09/2026", "", ""];
  // preenche a 1ª quantidade de cada exemplo, se houver algum tipo
  const linha = (base: string[], q: string): string => {
    const cels = [...base, ...tipos.map((_, i) => (i === 0 ? q : ""))];
    return cels.join(";");
  };
  return [cab.join(";"), linha(ex1, "40"), linha(ex2, "20")].join("\r\n");
}

/**
 * Lê o arquivo de importação de OS. `snap` só precisa das coleções
 * consultadas — a mesma folga de `Pick` que o resto do domínio usa.
 */
export function analisarImportacaoOS(
  texto: string,
  snap: Pick<Snapshot, "clientes" | "tiposPeca" | "lotes" | "paradas">,
): AnaliseImportacaoOS {
  const cruas = texto
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (cruas.length === 0) {
    return { grupos: [], colunasIgnoradas: [], erroArquivo: "O arquivo está vazio." };
  }

  const sep = cruas[0]!.includes("\t") ? "\t" : cruas[0]!.includes(";") ? ";" : ",";
  const cabecalho = cruas[0]!.split(sep).map((c) => c.trim());
  const cabNorm = cabecalho.map(norm);

  const posFixa = {} as Record<ColunaFixa, number>;
  for (const col of COLUNAS_FIXAS) posFixa[col] = cabNorm.indexOf(col);
  const faltando = COLUNAS_FIXAS.filter((c) => posFixa[c] < 0);
  if (faltando.length > 0) {
    return {
      grupos: [],
      colunasIgnoradas: [],
      erroArquivo: `O arquivo não tem a(s) coluna(s): ${faltando.join(", ")}.`,
    };
  }

  const porNomeOuCodigoCliente = new Map(
    snap.clientes.flatMap((c) => [
      [norm(c.nome), c] as const,
      [norm(c.codigo), c] as const,
    ]),
  );
  const porNomeTipo = new Map(
    snap.tiposPeca.filter((t) => t.ativo).map((t) => [norm(t.nome), t] as const),
  );
  const osExistentes = new Set(snap.lotes.map((l) => l.os));

  // Colunas de tipo: tudo no cabeçalho que não é coluna fixa nem vazio.
  const fixas = new Set<string>(COLUNAS_FIXAS);
  const colunasTipo: { indice: number; tipoId: string; tipoNome: string }[] = [];
  const colunasIgnoradas: ColunaIgnorada[] = [];
  const tipoJaVisto = new Set<string>();
  cabecalho.forEach((rotulo, i) => {
    if (rotulo === "" || fixas.has(cabNorm[i]!)) return;
    const tipo = porNomeTipo.get(cabNorm[i]!);
    if (!tipo) {
      colunasIgnoradas.push({ rotulo, motivo: "tipo de peça não encontrado" });
      return;
    }
    if (tipoJaVisto.has(tipo.id)) {
      colunasIgnoradas.push({ rotulo, motivo: "tipo repetido no cabeçalho" });
      return;
    }
    tipoJaVisto.add(tipo.id);
    colunasTipo.push({ indice: i, tipoId: tipo.id, tipoNome: tipo.nome });
  });

  const osUsadasNoArquivo = new Set<number>();
  const grupos: GrupoOS[] = [];

  cruas.slice(1).forEach((linha, idx) => {
    const numeroLinha = idx + 2;
    const partes = linha.split(sep).map((p) => p.trim());
    const campo = (c: ColunaFixa): string => partes[posFixa[c]] ?? "";

    const osRaw = campo("os");
    const invalido = (motivo: string): GrupoOSInvalido => ({
      valido: false,
      numeroLinha,
      os: osRaw,
      motivo,
    });

    const osNum = Number(osRaw);
    if (osRaw === "" || !Number.isInteger(osNum) || osNum < OS_MIN || osNum > OS_MAX) {
      grupos.push(invalido(`OS inválida — use um número de ${OS_MIN} a ${OS_MAX}.`));
      return;
    }
    if (osExistentes.has(osNum)) {
      grupos.push(invalido(`A OS ${osNum} já existe no sistema.`));
      return;
    }
    if (osUsadasNoArquivo.has(osNum)) {
      grupos.push(invalido(`A OS ${osNum} está repetida no arquivo.`));
      return;
    }

    const clienteRaw = campo("cliente");
    const cliente = porNomeOuCodigoCliente.get(norm(clienteRaw));
    if (!cliente) {
      grupos.push(invalido(`Hotel "${clienteRaw}" não encontrado.`));
      return;
    }

    const dataISO = paraDataISOBr(campo("data"));
    if (!dataISO) {
      grupos.push(invalido(`Data da coleta inválida: "${campo("data")}".`));
      return;
    }

    const horaRaw = campo("hora");
    if (horaRaw !== "" && !HORA.test(horaRaw)) {
      grupos.push(invalido(`Hora da coleta inválida: "${horaRaw}" (use HH:MM).`));
      return;
    }

    // Origem: em branco ou "Evento pontual" (nome antigo, ainda aceito) = Coleta
    // Avulsa. "relavagem" = OS de Relavagem — recolhida numa Parada programada
    // como qualquer outra (ADR-0015, corrigido na Fase 5), então a Parada do dia
    // é resolvida também nesse caso; não achar nenhuma só a deixa avulsa.
    const origemRaw = campo("origem");
    const origemNorm = norm(origemRaw);
    const eRelavagem = origemNorm === "relavagem";
    const paradasColeta = paradasDoCliente(snap, cliente.id).filter(
      (p) => p.acao === "coletar" || p.acao === "ambos",
    );
    let paradaEscolhida: (typeof paradasColeta)[number] | null = null;
    let origemNome = eRelavagem ? "Relavagem" : "Coleta Avulsa";
    if (origemRaw !== "" && origemNorm !== "evento pontual" && !eRelavagem) {
      const wd = diaSemanaISO(dataISO);
      const parada = paradasColeta.find(
        (p) => p.diasSemana.includes(wd) && (horaRaw === "" || p.horario === horaRaw),
      );
      if (!parada) {
        grupos.push(
          invalido(
            `A data ${campo("data")} não bate com nenhuma parada de coleta deste hotel${horaRaw ? ` às ${horaRaw}` : ""}.`,
          ),
        );
        return;
      }
      paradaEscolhida = parada;
      origemNome = `${descreveDias(parada.diasSemana)} às ${parada.horario}`;
    }
    if (eRelavagem) {
      const wd = diaSemanaISO(dataISO);
      const parada = paradasColeta.find(
        (p) => p.diasSemana.includes(wd) && (horaRaw === "" || p.horario === horaRaw),
      );
      if (parada) {
        paradaEscolhida = parada;
        origemNome = `Relavagem · ${descreveDias(parada.diasSemana)} às ${parada.horario}`;
      }
    }
    const paradaId = paradaEscolhida?.id ?? null;

    let coletaHora: string;
    if (horaRaw !== "") {
      coletaHora = horaRaw;
    } else if (paradaEscolhida) {
      coletaHora = paradaEscolhida.horario;
    } else {
      coletaHora = horaDe(new Date());
    }

    const sacosRaw = campo("sacos");
    let sacos: number | null = null;
    if (sacosRaw !== "") {
      const n = Number(sacosRaw);
      if (!Number.isInteger(n) || n < 0) {
        grupos.push(invalido(`Sacos inválido: "${sacosRaw}".`));
        return;
      }
      sacos = n;
    }

    // Itens: uma célula por coluna de tipo. Vazia ou "0" = o tipo não veio.
    const itens: ItemOSValido[] = [];
    const itensIgnorados: ItemOSIgnorado[] = [];
    for (const col of colunasTipo) {
      const cru = (partes[col.indice] ?? "").trim();
      if (cru === "") continue;
      const qtd = Number(cru);
      if (!Number.isInteger(qtd) || qtd < 0) {
        itensIgnorados.push({
          tipo: col.tipoNome,
          motivo: `quantidade inválida ("${cru}")`,
        });
        continue;
      }
      if (qtd === 0) continue;
      itens.push({ tipoId: col.tipoId, tipoNome: col.tipoNome, quantidade: qtd });
    }

    osUsadasNoArquivo.add(osNum);
    grupos.push({
      valido: true,
      numeroLinha,
      os: osNum,
      clienteId: cliente.id,
      clienteNome: cliente.nome,
      paradaId,
      origemNome,
      coletaData: dataISO,
      coletaHora,
      sacos,
      relavagem: eRelavagem,
      itens,
      itensIgnorados,
    });
  });

  return { grupos, colunasIgnoradas };
}
